// The Battle of Brickton, client half (internal/game/brickton.go is the
// server's): the town itself, the fires that eat it, and the words that
// narrate the raid.
//
// The server ships no geometry. Each city block arrives as an AA site of kind
// 8 — a position on the block's terrace, a district and a height class packed
// into `sub` — and this module dresses it the same way on every client: a
// paved terrace stepping down the hillside, four lots of the town art (the
// cottages, houses, shops, flats and the cathedral the land towns already
// use), and the rubble it becomes. Its HP rides the AA snapshot rows, so a
// block starts smoking as the bombs walk across it and burns all round once
// it falls. Everything here is gated on the round type being "brickton" —
// the air game never builds a block.

import * as THREE from "three";
import { hash2 } from "./terrain_core.js";
import { mergeBoxes, TEAM_INFO } from "./planes.js";
import { bakedGeometry, loadModel, modelsEnabled, ModelLOD, paintMaterialDS, setLodLevels } from "./models.js";
import { LOD_DIST, TOWN_CLASSES, TOWN_FOOTPRINT, loadLevels } from "./unit_models.js";

/** AA site kind of a Brickton city block (the server's AASiteCity). */
export const CITY_KIND = 8;
/** Half a block's built-up footprint (bricktonBlockHalfM): the lots stay inside it. */
export const BLOCK_HALF_M = 22;
/** Roofline per height class (bricktonClassH): cottages, houses, shops, the spire. */
export const CLASS_H = [8, 12, 16, 30];
// The terrace stands this far below the site point (the server puts the
// site 4 m above the block's highest ground).
const SITE_LIFT_M = 4;

export const blockDistrict = (sub) => (sub ?? 0) & 15;
export const blockClass = (sub) => ((sub ?? 0) >> 4) & 3;

/** Is this server running the Brickton round? (match.modes is ["brickton"]) */
export const isBricktonConfig = (config) => config?.match?.modes?.[0] === "brickton";

/** A district's name (config.brickton.districts), title case. */
export function districtName(config, d) {
  return config?.brickton?.districts?.[d] ?? `District ${d + 1}`;
}

// A block's own [0,1) dice: deterministic per block and draw.
const roll = (s, k) => hash2((s.x | 0) + k * 7919, (s.z | 0) - k * 104729, 0x5b1c7 >>> 0);

/**
 * The lots one block is built from, in WORLD coordinates: { x, z, w, d, h,
 * stem, yaw, sx, sz } — w along the model's door axis (X before the yaw),
 * h the TOWN_CLASSES height key. Pure and deterministic, so every client
 * dresses Brickton the same way. A cottage block leaves the odd lot as a yard.
 */
export function blockLots(s) {
  const cls = blockClass(s.sub);
  const out = [];
  const place = (lx, lz, w, d, h, stem, yaw) => {
    const [fx, fz, off = 0] = TOWN_FOOTPRINT[stem];
    const sx = w / fx, sz = d / fz;
    // Put the footprint's centre, not the model origin, on the lot centre.
    const cx = off * sx;
    out.push({
      x: s.x + lx - cx * Math.cos(yaw), z: s.z + lz + cx * Math.sin(yaw),
      w, d, h, stem, yaw, sx, sz,
    });
  };
  const fit = (w, d, h, k, hallOK) => {
    const cands = (TOWN_CLASSES[h] ?? TOWN_CLASSES[10]).filter((st) => st !== "town_hall" || hallOK);
    const scored = cands
      .map((stem) => {
        const [fx, fz] = TOWN_FOOTPRINT[stem];
        return { stem, err: Math.abs(Math.log(w / fx / (d / fz))) };
      })
      .sort((a, b) => a.err - b.err);
    const ties = scored.filter((c) => c.err - scored[0].err <= 0.25);
    return ties[Math.min(ties.length - 1, Math.floor(roll(s, k) * ties.length))].stem;
  };
  if (cls === 3) {
    // The cathedral block: the church along the north side, two houses south.
    place(0, -9, 27, 11, 12, "church", 0);
    for (const qx of [-1, 1]) {
      const w = 16 + roll(s, 11 + qx) * 3, d = 16 + roll(s, 13 + qx) * 3;
      place(qx * 11, 11, w, d, 10, fit(w, d, 10, 17 + qx, false), qx > 0 ? 0 : Math.PI);
    }
    return out;
  }
  const heights = [6, 10, 14];
  let k = 0;
  for (const qz of [-1, 1]) {
    for (const qx of [-1, 1]) {
      k++;
      const yard = cls === 0 ? 0.3 : cls === 1 ? 0.08 : 0;
      if (roll(s, 40 + k) < yard) continue; // a yard: the town has gardens
      // Mostly the block's class, now and then one notch lower.
      const h = heights[Math.max(0, cls - (roll(s, 50 + k) < 0.25 ? 1 : 0))];
      // Cottages and barns sit on smaller lots (a yard round them), so the
      // art isn't pulled twice its width; the land towns stretch it as far.
      const base = h === 6 ? 13 : 16;
      const w = base + roll(s, 60 + k) * 4, d = base + roll(s, 70 + k) * 4;
      // The square's first lot may be the town hall (its portico faces out).
      const stem = fit(w, d, h, 80 + k, cls === 2 && k === 1);
      place(qx * 11, qz * 11, w, d, h, stem, qx > 0 ? 0 : Math.PI);
    }
  }
  return out;
}

// Plaster variety across the town (multiplied into the art's paint).
const TINTS = [
  [1.0, 1.0, 1.0],
  [0.95, 0.9, 0.86],
  [0.86, 0.82, 0.78],
];

/** The box silhouette of a block's lots (the far level, and all of ?boxes). */
function lotBoxes(s, lots, y0) {
  const B = [];
  for (const b of lots) {
    const n = hash2(b.x | 0, b.z | 0, 8111);
    const wall = n < 0.33 ? 0xd1c2a3 : n < 0.66 ? 0xb5674d : 0xae9a85; // plaster, brick, stone
    const roof = n < 0.5 ? 0x7a4733 : 0x5c524c;
    const lx = b.x - s.x, lz = b.z - s.z;
    const along = Math.abs(Math.cos(b.yaw)) > 0.5;
    const W = along ? b.w : b.d, D = along ? b.d : b.w;
    if (b.stem === "church") {
      B.push({ x: lx, y: y0 + 5, z: lz, w: W, h: 10, d: D, color: 0xc9bfae });
      B.push({ x: lx, y: y0 + 11, z: lz, w: W + 0.6, h: 2, d: D * 0.7, color: 0x5c524c });
      B.push({ x: lx + W * 0.4, y: y0 + 12, z: lz, w: 5, h: 24, d: 5, color: 0xc9bfae }); // the tower
      B.push({ x: lx + W * 0.4, y: y0 + 26, z: lz, w: 3, h: 4, d: 3, color: 0x4d5a52 }); // its spire
      continue;
    }
    const wallH = b.h - 2;
    B.push({ x: lx, y: y0 + wallH / 2, z: lz, w: W, h: wallH, d: D, color: wall });
    B.push({ x: lx, y: y0 + wallH + 0.7, z: lz, w: W + 0.8, h: 1.4, d: D + 0.8, color: roof });
    B.push({ x: lx, y: y0 + wallH + 1.9, z: lz, w: W * 0.55, h: 1.2, d: D * 0.55, color: roof });
    for (let lvl = 2.2; lvl < wallH - 1; lvl += 3.6) {
      B.push({ x: lx, y: y0 + lvl, z: lz + D / 2 + 0.06, w: W * 0.7, h: 1.0, d: 0.12, color: 0x292e38 });
      B.push({ x: lx, y: y0 + lvl, z: lz - D / 2 - 0.06, w: W * 0.7, h: 1.0, d: 0.12, color: 0x292e38 });
    }
  }
  return B;
}

/** What's left of a block: heaps of brick, charred beams, a wall stub or two. */
function rubbleBoxes(s, lots, y0) {
  const B = [];
  let k = 0;
  for (const b of lots) {
    const lx = b.x - s.x, lz = b.z - s.z;
    const W = Math.min(b.w, b.d);
    k++;
    B.push({ x: lx, y: y0 + 0.9, z: lz, w: W * 0.9, h: 1.8, d: W * 0.8, color: 0x4a4038 });
    B.push({ x: lx + 2, y: y0 + 2.2, z: lz - 1.5, w: W * 0.45, h: 1.6, d: W * 0.4, color: 0x2a2420 });
    B.push({ x: lx - W * 0.3, y: y0 + 1.6, z: lz + W * 0.25, w: 3, h: 3.2, d: 2.4, color: 0x1c1916 });
    if (roll(s, 90 + k) < 0.6) {
      // A gable end still standing, blackened, with nothing behind it.
      B.push({ x: lx + W * 0.42, y: y0 + b.h * 0.35, z: lz, w: 0.8, h: b.h * 0.7, d: W * 0.7, color: 0x3b3430 });
    }
  }
  return B;
}

// Baked town-art geometry in model space, shared by every block: "stem#lvl".
const bakedCache = new Map();
function bakedLocal(stem, lvl, tpl) {
  const key = `${stem}#${lvl}`;
  if (!bakedCache.has(key)) bakedCache.set(key, bakedGeometry(tpl).geometry);
  return bakedCache.get(key);
}

/** Concatenate geometries (position/normal/color/index), tinting each part. */
export function mergeTinted(parts) {
  let verts = 0, idx = 0;
  for (const { geo } of parts) {
    verts += geo.attributes.position.count;
    idx += geo.index ? geo.index.count : geo.attributes.position.count;
  }
  const pos = new Float32Array(verts * 3), nor = new Float32Array(verts * 3), col = new Float32Array(verts * 3);
  const index = new Uint32Array(idx);
  let vo = 0, io = 0;
  for (const { geo, tint } of parts) {
    const n = geo.attributes.position.count;
    pos.set(geo.attributes.position.array, vo * 3);
    nor.set(geo.attributes.normal.array, vo * 3);
    const c = geo.attributes.color.array;
    for (let i = 0; i < n * 3; i++) col[vo * 3 + i] = c[i] * (tint ? tint[i % 3] : 1);
    if (geo.index) for (let i = 0; i < geo.index.count; i++) index[io + i] = geo.index.getX(i) + vo;
    else for (let i = 0; i < n; i++) index[io + i] = i + vo;
    io += geo.index ? geo.index.count : n;
    vo += n;
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  out.setAttribute("normal", new THREE.BufferAttribute(nor, 3));
  out.setAttribute("color", new THREE.BufferAttribute(col, 3));
  out.setIndex(new THREE.BufferAttribute(index, 1));
  out.computeBoundingSphere();
  return out;
}

const terrainMat = new THREE.MeshLambertMaterial({ vertexColors: true });

/**
 * Build one city block for the AA renderer: `root` stands on the block's
 * terrace (the site lifted down by SITE_LIFT_M); `pad` is the paving and its
 * plinth down the hill (always drawn), `intact` the houses, `wreck` the
 * rubble. `onDressed(intact)` fires if the town art arrives to replace the
 * box stand-ins (the art's wrecks join the rubble on their own). `pitch` is the street grid (the pad's full width).
 */
export function buildCityBlock(s, field, { pitch = 62, onDressed = null, material = terrainMat } = {}) {
  const lots = blockLots(s);
  const root = new THREE.Group();
  const y0 = s.y - SITE_LIFT_M;
  root.position.set(s.x, y0, s.z);
  // The terrace: paving the full pitch wide (the streets are the margin round
  // the lots), and a brick plinth down to the lowest ground under it so a
  // hillside block steps rather than floats.
  let low = y0;
  if (field) {
    const h = pitch / 2;
    for (const [dx, dz] of [[0, 0], [-h, -h], [h, -h], [-h, h], [h, h], [0, h], [0, -h], [h, 0], [-h, 0]]) {
      low = Math.min(low, field.surfaceAt(s.x + dx, s.z + dz));
    }
  }
  const depth = Math.max(0.4, y0 - low + 1.5);
  const pad = new THREE.Mesh(
    mergeBoxes([
      { x: 0, y: -depth / 2, z: 0, w: pitch, h: depth, d: pitch, color: 0x8a5a44 },
      { x: 0, y: 0.05, z: 0, w: pitch - 2, h: 0.12, d: pitch - 2, color: 0x77726a }, // cobbles
      { x: 0, y: 0.1, z: 0, w: BLOCK_HALF_M * 2 + 2, h: 0.12, d: BLOCK_HALF_M * 2 + 2, color: 0x9a948a }, // the kerb
    ]),
    material,
  );
  root.add(pad);
  const boxes = new THREE.Mesh(mergeBoxes(lotBoxes(s, lots, 0.15)), material);
  const intact = new THREE.Group();
  intact.add(boxes);
  root.add(intact);
  const wreck = new THREE.Group();
  wreck.add(new THREE.Mesh(mergeBoxes(rubbleBoxes(s, lots, 0.15)), material));
  wreck.visible = false;
  root.add(wreck);
  if (modelsEnabled()) {
    dressBlock(s, lots, boxes).then((dressed) => {
      if (!dressed) return;
      if (dressed.wreck) wreck.add(dressed.wreck);
      onDressed?.(dressed.intact);
    });
  }
  return { root, pad, intact, wreck, lots };
}

// Swap a block's boxes for the town art: three merged levels of detail with
// the boxes kept as the far level, and the art's own wreck models (if they
// came) laid over the rubble.
async function dressBlock(s, lots, boxes) {
  const stems = [...new Set(lots.map((l) => l.stem))];
  const loaded = await Promise.all(stems.map((st) => loadLevels(st)));
  if (loaded.some((l) => !l)) return null;
  const tpls = new Map(stems.map((st, i) => [st, loaded[i]]));
  const m = new THREE.Matrix4(), q = new THREE.Quaternion(), up = new THREE.Vector3(0, 1, 0);
  const p = new THREE.Vector3(), sc = new THREE.Vector3();
  const lotMatrix = (l) => {
    q.setFromAxisAngle(up, l.yaw);
    return m.clone().compose(p.set(l.x - s.x, 0.15, l.z - s.z), q, sc.set(l.sx, 1, l.sz));
  };
  const levelMesh = (lvl) => {
    const parts = lots.map((l) => {
      const t = tpls.get(l.stem);
      let use = lvl;
      while (use > 0 && !t[use]) use--;
      const geo = bakedLocal(l.stem, use, t[use]).clone().applyMatrix4(lotMatrix(l));
      return { geo, tint: TINTS[Math.min(2, Math.floor(hash2(l.x | 0, l.z | 0, 8111) * 3))] };
    });
    const out = new THREE.Mesh(mergeTinted(parts), paintMaterialDS);
    for (const pt of parts) pt.geo.dispose();
    return out;
  };
  const lod = new ModelLOD();
  const [d1, d2, d3] = LOD_DIST.building;
  setLodLevels(lod, [[levelMesh(0), 0], [levelMesh(1), d1], [levelMesh(2), d2], [boxes, d3]]);
  // The wrecks: the art ships a `_wreck` beside every town building.
  let wreck = null;
  const wrecks = await Promise.all(stems.map((st) => loadModel(`${st}_wreck`).catch(() => null)));
  if (wrecks.every(Boolean)) {
    const wt = new Map(stems.map((st, i) => [st, wrecks[i]]));
    const parts = lots.map((l) => ({
      geo: bakedLocal(`${l.stem}_wreck`, 0, wt.get(l.stem)).clone().applyMatrix4(lotMatrix(l)),
      tint: [0.55, 0.5, 0.46], // soot
    }));
    wreck = new THREE.Mesh(mergeTinted(parts), paintMaterialDS);
    for (const pt of parts) pt.geo.dispose();
  }
  return { intact: lod, wreck };
}

/**
 * The town burning: smoke off every block the bombs have hurt, fire and a
 * thick column off every block that has fallen. Driven by the AA rows' HP, so
 * it needs no wire of its own; LOD-gated by distance like the crash fires.
 */
export class CityFires {
  constructor(particles, maxHp = 250) {
    this.particles = particles;
    this.maxHp = Math.min(255, maxHp || 250);
    this.next = new Map(); // block id -> seconds to its next puff
    this._v = new THREE.Vector3();
  }

  update(dt, cam, guns) {
    for (const [id, g] of guns) {
      if (g.kind !== CITY_KIND) continue;
      const hp = g.alive ? (g.hp ?? this.maxHp) : 0;
      const frac = hp / this.maxHp;
      if (frac > 0.8) continue;
      const s = g.site;
      const dx = s.x - cam.x, dz = s.z - cam.z;
      if (dx * dx + dz * dz > 4000 * 4000) continue;
      let t = (this.next.get(id) ?? 0) - dt;
      if (t <= 0) {
        const burning = frac < 0.45;
        t = !g.alive ? 0.3 : burning ? 0.55 : 1.1;
        const y = s.y - SITE_LIFT_M;
        const jx = (Math.random() - 0.5) * 30, jz = (Math.random() - 0.5) * 30;
        if (burning) this.particles.fire(this._v.set(s.x + jx, y + 2, s.z + jz), { size: g.alive ? 4 : 6 });
        this.particles.smoke(this._v.set(s.x + jx, y + (g.alive ? 10 : 6), s.z + jz), {
          size: g.alive ? 6 : 10,
          alpha: g.alive ? 0.55 : 0.85,
        });
      }
      this.next.set(id, t);
    }
  }

  clear() {
    this.next.clear();
  }
}

/** Town centre and the side holding it, read off the city blocks on the field. */
export function cityPicture(guns) {
  let n = 0, x = 0, z = 0, defender = -1, alive = 0;
  for (const g of guns.values()) {
    if (g.kind !== CITY_KIND) continue;
    n++;
    x += g.site.x;
    z += g.site.z;
    defender = g.team;
    if (g.alive) alive++;
  }
  if (!n) return null;
  return { x: x / n, z: z / n, defender, attacker: 1 - defender, blocks: n, standing: alive };
}

// ---- the words -------------------------------------------------------------

/**
 * The round's score line, read off the server's books: the raid's points
 * are the burn in per mille, the defense's the raiders it has downed.
 */
export function bricktonLine(score, attacker, config) {
  const b = config?.brickton ?? {};
  const burn = ((attacker === 0 ? score.c : score.b) ?? 0) / 1000;
  const lost = (attacker === 0 ? score.b : score.c) ?? 0;
  return {
    attacker,
    burn,
    burnToWin: b.burnToWin ?? 0.7,
    lost,
    tickets: b.raidTickets ?? 32,
  };
}

/** The role call at the top of a round, from one pilot's side of it. */
export function roleCopy(attacker, selfTeam, config) {
  const pct = Math.round((config?.brickton?.burnToWin ?? 0.7) * 100);
  const tickets = config?.brickton?.raidTickets ?? 32;
  if (selfTeam === attacker) {
    return {
      banner: `⚑ RAID BRICKTON — burn ${pct}% of the town before the bell`,
      hint: `Bombers on the rooftops, fighters on their wing. Bay empty? Overfly your home field to bomb up. Lose ${tickets} aircraft and the raid is broken.`,
      color: "#f4c542",
    };
  }
  return {
    banner: `⚑ DEFEND BRICKTON — shoot down the raid, hold till the bell`,
    hint: `Get on the bombers before they reach the town. Down ${tickets} raiders and the raid breaks; the city flak is yours to man.`,
    color: "#8fd0ff",
  };
}

/** The bell, said as what happened to the town. */
export function endCopy(reason, winner) {
  const tn = (TEAM_INFO[winner]?.name ?? "").toUpperCase();
  switch (reason) {
    case "razed":
      return `🔥 BRICKTON BURNS — THE ${tn} RAID WINS`;
    case "repulsed":
      return `✈ THE RAID IS BROKEN — ${tn} SAVES BRICKTON`;
    case "held":
      return `⚑ BRICKTON STANDS — ${tn} HELD TO THE BELL`;
  }
  return `${tn} WINS THE ROUND`;
}
